/** The right-hand property rails: the frame Inspector, the element panel
 *  (Design, HTML and Prototype tabs), its flex, image and interaction
 *  controls, the element code view and the shared property fields. */
export const inspector = {
  // frame inspector
  frameName: 'Frame name',
  lockedTitle: 'Locked — can’t be moved, resized or deleted on the canvas',
  locked: 'Locked',
  unlock: 'Unlock',
  groupDevelopment: 'Development',
  groupExport: 'Export',
  groupMemory: 'Memory',
  downloadPng: 'Download as PNG (2×)',
  downloadJpg: 'Download as JPG (2×)',
  copyPngTitle: 'Copy the image (2×) to paste anywhere',
  copyPng: 'Copy as PNG',
  copyImageUrlTitle:
    'Public image URL — always renders the current design; paste it as og:image or a blog featured image',
  copyImageUrl: 'Copy image URL',
  pinReferenceTitle: 'Add to design memory — will be used as reference',
  pinReference: 'Pin as reference',
  hideHtmlEditor: 'Hide the HTML editor',
  editHtml: 'Edit the HTML',
  code: 'Code',
  copyHtmlTitle: 'Copy the frame’s HTML',
  exportHtmlTitle: 'Save the frame’s HTML as a file',
  export: 'Export',
  copyHtmlFailed: 'Couldn’t copy the HTML. Select it in the editor and copy it by hand.',
  saveFailed: 'Couldn’t save the design. Please try again.',
  saving: 'saving…',
  saved: 'saved',
  lastEditBy: 'last edit by {name}',
  history: 'History',
  unlockToDelete: 'Unlock the frame to delete it',
  xPosition: 'X position',
  yPosition: 'Y position',
  width: 'Width',
  height: 'Height',

  // element panel
  tabDesign: 'Design',
  tabHtml: 'HTML',
  tabPrototype: 'Prototype',
  inParent: 'in {parent}',
  selectParent: 'Select parent',
  readingElement: 'Reading the element…',
  sectionAlignment: 'Alignment',
  sectionPosition: 'Position',
  sectionLayout: 'Layout',
  sectionAppearance: 'Appearance',
  sectionFill: 'Fill',
  sectionStroke: 'Stroke',
  sectionText: 'Text',
  rowType: 'Type',
  rowOrder: 'Order',
  rowMinWidth: 'Min width',
  rowDisplay: 'Display',
  rowGap: 'Gap',
  rowOpacity: 'Opacity',
  rowRadius: 'Radius',
  rowColor: 'Color',
  rowWidth: 'Width',
  rowFont: 'Font',
  rowSize: 'Size',
  rowAlign: 'Align',
  layerAlignment: 'Layer alignment within parent',
  alignLayerLeft: 'Align layer left',
  centerLayerHorizontally: 'Center layer horizontally',
  alignLayerRight: 'Align layer right',
  alignLayerTop: 'Align layer top',
  centerLayerVertically: 'Center layer vertically',
  alignLayerBottom: 'Align layer bottom',
  withinParent: '{label} within parent',
  positionTitle: 'Rendered position relative to parent, in frame pixels',
  rotation: 'Rotation',
  flipHorizontally: 'Flip horizontally',
  flipVertically: 'Flip vertically',
  positionType: 'Position type',
  orderOf: 'of {count}',
  unlinkSize: 'Unlink width and height',
  linkSize: 'Link width and height',
  linkSizeDisabled: 'Set a positive width and height to link them',
  widthSizing: 'Width sizing',
  heightSizing: 'Height sizing',
  sizeAuto: 'Auto',
  sizeFixed: 'Fixed',
  sizeFill: 'Fill',
  sizeHug: 'Hug',
  minimumWidth: 'Minimum width',
  display: 'Display',
  rowGapLabel: 'Row gap',
  columnGap: 'Column gap',
  hideElement: 'Hide element',
  showElement: 'Show element',
  elementOpacity: 'Element opacity',
  cornerRadius: 'Corner radius',
  clipContent: 'Clip content',
  fillColor: 'Fill color',
  fillOpacity: 'Fill opacity',
  borderColor: 'Border color',
  borderWidth: 'Border width',
  sides: 'Sides: {sides}',
  sideTop: 'top',
  sideRight: 'right',
  sideBottom: 'bottom',
  sideLeft: 'left',
  fontFamily: 'Font family',
  fontGroupCustom: 'Custom fonts',
  fontGroupFrame: 'In this frame',
  fontGroupWeb: 'Fonts',
  fontGroupDevice: 'Device fonts (vary by viewer)',
  fontSystemUi: 'System UI',
  fontSystemMono: 'System monospace',
  fontSize: 'Font size',
  fontWeight: 'Font weight',
  textAlignment: 'Text alignment',
  alignLeft: 'Align left',
  alignCenter: 'Align center',
  alignRight: 'Align right',
  justify: 'Justify',

  // padding and flex layout
  padding: 'Padding',
  individualPadding: 'Individual padding',
  individualPaddingTitle: 'Edit padding on each side',
  paddingTop: 'Padding top',
  paddingRight: 'Padding right',
  paddingBottom: 'Padding bottom',
  paddingLeft: 'Padding left',
  verticalPadding: 'Vertical padding',
  horizontalPadding: 'Horizontal padding',
  mixed: 'Mixed',
  flexLayout: 'Flex layout',
  wrapChildren: 'Wrap children',
  wrapReverse: 'Wrapping in reverse order',
  flexDirection: 'Flex direction',
  rowDirection: 'Row direction',
  columnDirection: 'Column direction',
  reverseDirection: 'Reverse direction',
  alignment: 'Alignment',
  flexAlignment: 'Flex alignment',
  alignChildren: 'Align children {vertical} {horizontal}',
  alignKeep: '{label} (keep {spacing})',
  vTop: 'top',
  vMiddle: 'middle',
  vBottom: 'bottom',
  hLeft: 'left',
  hCenter: 'center',
  hRight: 'right',
  crossAxisAlignment: 'Cross-axis alignment',
  crossStart: 'Start',
  crossCenter: 'Center',
  crossEnd: 'End',
  crossStretch: 'Stretch',
  crossBaseline: 'Baseline',
  gap: 'Gap',
  flexSpacing: 'Flex spacing',
  spacingFixed: 'Fixed gap',
  spacingBetween: 'Space between',
  spacingAround: 'Space around',
  spacingEvenly: 'Space evenly',
  lineGap: 'Line gap',

  // image fill
  imageSizeFill: 'Fill',
  imageSizeFit: 'Fit',
  imageSizeStretch: 'Stretch',
  imageSizeOriginal: 'Original size',
  imageSizeScaleDown: 'Scale down',
  imageSizeTile: 'Tile',
  imageSizeCustom: 'Custom',
  imageUrlInvalid: 'Enter a valid image URL.',
  imageUrlProtocol: 'Use an http or https image URL.',
  imageUrlTimeout: 'Image took too long to load. Try another URL.',
  imageUrlLoadFailed: 'Couldn’t load that image. Check the URL and try again.',
  imageTooLarge: 'Choose an image smaller than 5 MB.',
  imageDesignChanged: 'The design changed. Select the image and try again.',
  imageNotEditable: 'The image is no longer editable. Select it and try again.',
  imageReplaceFailed: 'Couldn’t replace the image. Please try again.',
  chooseReplacementImage: 'Choose replacement image',
  imageFill: 'Image fill',
  editImageFill: 'Edit image fill',
  image: 'Image',
  imageFillSettings: 'Image fill settings',
  closeImageSettings: 'Close image settings',
  imageUrl: 'Image URL',
  applyImageUrl: 'Apply image URL',
  uploadImage: 'Upload image',
  currentImage: 'Current image',
  uploading: 'Uploading…',
  loading: 'Loading…',
  sizing: 'Sizing',
  imageSizing: 'Image sizing',

  // prototype interactions
  triggerClick: 'On click',
  triggerHover: 'On hover',
  triggerPress: 'While pressing',
  triggerDelay: 'After delay',
  triggerKey: 'On key',
  actionNavigate: 'Navigate to',
  actionOverlay: 'Open overlay',
  actionBack: 'Back',
  actionCloseOverlay: 'Close overlay',
  actionScrollTo: 'Scroll to',
  actionUrl: 'Open link',
  transitionInstant: 'Instant',
  transitionDissolve: 'Dissolve',
  transitionSlideLeft: 'Slide left',
  transitionSlideRight: 'Slide right',
  transitionSlideUp: 'Slide up',
  transitionSlideDown: 'Slide down',
  transitionPushLeft: 'Push left',
  transitionPushRight: 'Push right',
  layerNotEditable: 'This layer can’t be edited right now',
  interactions: 'Interactions',
  addInteraction: 'Add interaction',
  scriptMade: 'A script creates this element, so it has no markup to hold interactions. Add them in the frame’s HTML.',
  unlockToAdd: 'Unlock the layer to add interactions.',
  noInteractions: 'Nothing happens when this is clicked yet.',
  flow: 'Flow',
  flowStartRow: 'Start',
  startsHere: 'Starts here',
  startFlowHere: 'Start the flow on this frame',
  missingFrame: 'Missing frame ({id})',
  interactionN: 'Interaction {n}',
  rowTrigger: 'Trigger',
  triggerN: 'Trigger {n}',
  removeInteractionN: 'Remove interaction {n}',
  rowAfter: 'After',
  delayN: 'Delay {n}',
  rowKey: 'Key',
  keyN: 'Key {n}',
  rowAction: 'Action',
  actionN: 'Action {n}',
  rowFrame: 'Frame',
  targetFrameN: 'Target frame {n}',
  addAnotherFrame: 'Add another frame first',
  rowElement: 'Element',
  scrollTargetN: 'Scroll target {n}',
  rowUrl: 'URL',
  linkN: 'Link {n}',
  rowMotion: 'Motion',
  transitionN: 'Transition {n}',
  durationN: 'Duration {n}',

  // element code view
  codeCopied: 'Code copied',
  copyCode: 'Copy code',
  codeCopiedStatus: 'Code copied to clipboard',

  // property fields
  scrubHint: 'Drag to adjust · Shift: ×10 · Alt/Option: ×0.1 · Esc: cancel',
  pickLabel: 'Pick {label}',
  pickColour: 'Pick colour',
  colorNone: 'none',
} as const
